import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gem, Bomb, Minus, Plus } from "lucide-react";
import { api, errMsg, fmtAZN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BetControl } from "./BetControl";
import { sfx } from "@/lib/sound";

const Tile = ({ i, g, onClick }) => {
  const revealed = g?.revealed.includes(i);
  const over = g && g.status !== "active";
  const mine = over && g.mine_positions?.includes(i);
  const open = revealed || over;
  return (
    <motion.button whileHover={!open ? { scale: 1.06, y: -2 } : {}} whileTap={{ scale: 0.94 }} onClick={() => onClick(i)} disabled={!g || over || revealed}
      animate={{ rotateY: open ? 180 : 0 }} transition={{ duration: 0.45 }} style={{ transformStyle: "preserve-3d", transformPerspective: 600 }}
      className="relative aspect-square rounded-xl" data-testid={`mines-tile-${i}`}>
      <div className="absolute inset-0 rounded-xl bg-[#1d1d1d] border border-white/10 shadow-[inset_0_-4px_0_rgba(0,0,0,0.5)]" style={{ backfaceVisibility: "hidden" }} />
      <div className={`absolute inset-0 rounded-xl flex items-center justify-center border ${mine ? (g.hit === i ? "bg-red-600/40 border-red-500" : "bg-red-900/20 border-red-900/50") : revealed ? "bg-emerald-500/15 border-emerald-400/60" : "bg-white/5 border-white/5 opacity-40"}`}
        style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
        {mine ? <Bomb className="text-red-400 w-1/2 h-1/2" /> : <Gem className="text-emerald-400 w-1/2 h-1/2 drop-shadow-[0_0_10px_#10b981]" />}
      </div>
    </motion.button>
  );
};

export const MinesGame = ({ mode }) => {
  const { updateUser } = useAuth();
  const [g, setG] = useState(null);
  const [bet, setBet] = useState(1);
  const [mines, setMines] = useState(3);
  const [busy, setBusy] = useState(false);
  const active = g?.status === "active";

  useEffect(() => { api.get("/mines/active").then(({ data }) => data && setG(data)); }, []);

  const call = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mines/${path}`, body);
      setG((prev) => (path === "start" ? data : { ...data, hit: data.hit ?? prev?.hit }));
      updateUser({ balance: data.balance, demo_balance: data.demo_balance });
      if (data.status === "lost") { sfx.explode(); toast.error("Mina! Mərc uduzuldu"); }
      else if (data.status === "won") { sfx.cashout(); toast.success(`Qazanc: +${fmtAZN(data.win)}`); }
      else if (path === "reveal") sfx.gem();
      else sfx.click();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="mines-game">
      <div className="rounded-3xl border border-white/10 bg-[#111]/90 backdrop-blur-xl p-6 space-y-6 order-2 lg:order-1">
        <div><p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Mərc</p><BetControl value={bet} onChange={setBet} disabled={active} testid="mines-bet" /></div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Mina sayı</p>
          <div className="flex items-center gap-2">
            <button disabled={active} onClick={() => setMines((m) => Math.max(1, m - 1))} className="btn-ghost rounded-full p-2 disabled:opacity-40" data-testid="mines-count-dec"><Minus size={14} /></button>
            <span className="flex-1 text-center font-display font-bold text-2xl" data-testid="mines-count">{active ? g.mines : mines}</span>
            <button disabled={active} onClick={() => setMines((m) => Math.min(24, m + 1))} className="btn-ghost rounded-full p-2 disabled:opacity-40" data-testid="mines-count-inc"><Plus size={14} /></button>
          </div>
        </div>
        {active && (
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="rounded-xl bg-black/40 p-3"><p className="text-[10px] uppercase text-zinc-500">Cari</p><p className="font-display font-bold text-emerald-400" data-testid="mines-multiplier">x{g.multiplier}</p></div>
            <div className="rounded-xl bg-black/40 p-3"><p className="text-[10px] uppercase text-zinc-500">Növbəti</p><p className="font-display font-bold text-[#FFD700]">x{g.next_multiplier}</p></div>
          </div>
        )}
        {active ? (
          <button disabled={busy || !g.revealed.length} onClick={() => call("cashout", { game_id: g.game_id })} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="mines-cashout-btn">
            Götür {fmtAZN(g.bet * g.multiplier)}
          </button>
        ) : (
          <button disabled={busy} onClick={() => call("start", { bet, mines, mode })} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="mines-start-btn">Oyuna başla</button>
        )}
      </div>
      <div className="rounded-3xl machine-frame p-4 sm:p-8 order-1 lg:order-2">
        <div className="grid grid-cols-5 gap-2 sm:gap-3 max-w-[560px] mx-auto">
          {Array.from({ length: 25 }, (_, i) => <Tile key={i} i={i} g={g} onClick={(t) => !busy && call("reveal", { game_id: g.game_id, tile: t })} />)}
        </div>
        {!g && <p className="text-center text-zinc-500 text-sm mt-6">Mərc və mina sayını seçib oyuna başlayın</p>}
      </div>
    </div>
  );
};
